import pull from 'lodash/pull';
import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';
import trim from 'lodash/trim';
import uniq from 'lodash/uniq';
import take from 'lodash/take';

import { Component, ComponentFactoryResolver, Inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ISelectItem } from '@cakeiteasy/web-ui';
import {
  CieUtilsModalManagerService,
  CustomComponent,
  REMINDER_REASONS,
  USERS_PROFILE_SERVICE_TOKEN,
  UsersProfileServiceInterface,
  IReminderGroup,
  IReminderGroupReminder,
} from '@cakeiteasy/web-utils';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

import { IUser } from '../../../users/models/i-user';
import { getUser } from '../../../users/store/selectors/user.selectors';
import { UserApiService } from '../../../api-services/services/user-api.service';
import { UserState } from '../../../users/store/reducers/user.reducer';

import { UpsertCalendarModalComponent } from '../../modals/upsert-calendar-modal/upsert-calendar-modal.component';
import { ImportRemindersModalComponent } from '../../modals/import-reminders-modal/import-reminders-modal.component';
import { debounceTime, filter } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'cake-reminders',
  templateUrl: './cake-reminders.component.html',
  styleUrls: ['./cake-reminders.component.scss']
})
export class CakeRemindersComponent extends CustomComponent implements OnInit {
  public groups: ISelectItem[];
  public currentGroup: ISelectItem;
  public reminders: IReminderGroupReminder[];
  public reminderReasons: ISelectItem[];
  public searchRemindersQuery: string;
  public onReminderGroupFieldUpdated = new Subject<{ group: IReminderGroup, isValid: boolean }>();

  public groupsLoading: Promise<any>;
  public groupSaving: Promise<any>;
  public remindersLoading: Promise<any>;
  public reminderSaving: Promise<any>;
  public countryCode: string;
  public busy: Promise<any>;

  private _currentUser: IUser;

  constructor(@Inject(USERS_PROFILE_SERVICE_TOKEN) public profileService: UsersProfileServiceInterface,
              private _userService: UserApiService,
              private _userStore: Store<UserState>,
              private _translateService: TranslateService,
              private _componentFactory: ComponentFactoryResolver,
              private _modalManager: CieUtilsModalManagerService) {
    super();
  }

  async ngOnInit() {
    this.countryCode = await (this.busy = this.profileService.getCountryCode());

    this.reminderReasons = [{
      value: REMINDER_REASONS.BIRTHDAY,
      label: this._translateService.instant('profile.cakeCalendars.occasions.birthday'),
    }, {
      value: REMINDER_REASONS.JUBILEE,
      label: this._translateService.instant('profile.cakeCalendars.occasions.jubilee'),
    }];

    this._activate();
    this._subscribe();
  }

  public createReminder(reminder: IReminderGroupReminder) {
    let creatingReminder = {
      ...reminder,
      reminderGroup: this.currentGroup.ref.id
    };

    this.reminderSaving = this._userService.reminderGroupsReminder('post', this.currentGroup.ref.id,
      null,
      creatingReminder)
      .then((created) => {
        this.reminders[0].id = created.id;
        this._pushNew();
      });
  }

  public updateReminder(reminder: IReminderGroupReminder) {
    this.reminderSaving = this._userService.reminderGroupsReminder('patch',
      this.currentGroup.ref.id,
      reminder.id,
      reminder);
  }

  public removeReminder(reminderId: number) {
    let reminder = this.reminders.find((reminder) => reminder.id === reminderId);
    pull(this.reminders, reminder);

    this._userService.reminderGroupsReminder('remove',
      this.currentGroup.ref.id,
      reminder.id)
      .catch(() => {
        this.reminders.unshift(reminder);
      });
  }

  public openGroup(group: ISelectItem) {
    this.currentGroup = group;

    this._loadGroupReminders(group.value)
      .then((reminders) => {
        this.reminders = reminders;
        this._pushNew();
      });
  }

  public showImportReminders() {
    this._modalManager
      .show(ImportRemindersModalComponent, this._componentFactory,
        { reminderGroupId: this.currentGroup.ref.id, reminderReasons: this.reminderReasons },
        { windowClass: 'modal-simple', backdrop: 'static' })
      .then(() => {
        this.openGroup(this.currentGroup);
      });
  }

  public upsertGroup(group: IReminderGroup) {
    let isNew = !group;
    this._modalManager
      .show(UpsertCalendarModalComponent,
        this._componentFactory,
        {
          group: cloneDeep(group),
          currentUserEmail: this._currentUser.email
        },
        { windowClass: 'modal-simple', backdrop: 'static' })
      .then((result) => {
        if (isNew) {
          return this.groupsLoading = this._userService.reminderGroups('post', null, result)
            .then((created) => {
              this.groups.unshift(this._mapGroup(created));
              this.openGroup(this.groups[0]);
            });
        } else {
          return this.groupSaving = this._userService.reminderGroups('patch', result.id, { title: result.title })
            .then((updated) => {
              let listGroupIndex = findIndex(this.groups, { value: updated.id });

              let updatedListItem = this._mapGroup(updated);
              this.groups.splice(listGroupIndex, 1, updatedListItem);
              this.currentGroup = this.groups[listGroupIndex];
            });
        }
      });
  }

  public removeGroup(group: IReminderGroup) {
    let listGroup = this.groups.find(selectItem => selectItem.value === group.id);
    pull(this.groups, listGroup);
    if (this.currentGroup && this.currentGroup.value === group.id && this.groups.length > 0) {
      this.openGroup(this.groups[0]);
    }
    if (this.groups.length === 0) {
      this.reminders = [];
      this.currentGroup = null;
    }

    this._userService.reminderGroups('remove', group.id)
      .catch(() => {
        this.groups.unshift(listGroup);
        this.openGroup(this.groups[0]);
      });
  }

  public onSmsPhoneChange(form: any): void {
    const group = { ...this.currentGroup.ref };
    const trimmedSmsPhone = group.smsPhone
      ? group.smsPhone.toString().trim()
      : '';
    group.smsPhone = trimmedSmsPhone;
    group.smsSubscribe = Boolean(trimmedSmsPhone);
    this.onReminderGroupFieldUpdated.next({
      group,
      isValid: form.valid
    });
  }

  private _subscribe() {
    const reminderGroupSub = this.onReminderGroupFieldUpdated
      .pipe(debounceTime(300))
      .pipe(filter((data) => data.isValid))
      .subscribe((data) => {
        let emails = (data.group.recipientEmails as string)
          .split(',')
          .map((email) => trim(email));

        this.groupSaving = this._userService.reminderGroups('patch', data.group.id, {
          recipientEmails: take(uniq(emails), 5),
          daysToNotice: data.group.daysToNotice,
          smsSubscribe: data.group.smsSubscribe,
          smsPhone: data.group.smsPhone
        });
      });

    const userSub = this._userStore.select(getUser).subscribe((user) => {
      this._currentUser = user;
    });

    this.addSubscriber(reminderGroupSub);
    this.addSubscriber(userSub);
  }

  private _activate() {
    this._loadGroups()
      .then((groups) => {
        this.groups = groups.map((group) => this._mapGroup(group));

        if (this.groups.length) {
          this.openGroup(this.groups[0]);
        }
      });
  }

  private _mapGroup(group): ISelectItem {
    group.recipientEmails = group.recipientEmails.join(', ');
    return {
      label: group.title,
      value: group.id,
      ref: group
    };
  }

  private _pushNew() {
    this.reminders.unshift({
      reminderGroup: null,
      personName: ''
    });
  }

  private _loadGroups(): Promise<IReminderGroup[]> {
    return this.groupsLoading = this._userService.reminderGroups('get');
  }

  private _loadGroupReminders(groupId): Promise<IReminderGroupReminder[]> {
    return this.remindersLoading = this._userService.reminderGroupsReminder('get', groupId);
  }
}
